import { Dropdown, Icon, Menu, Table } from "antd";
import moment from "moment";
import React, { useCallback } from "react";
import { Link } from "react-router-dom";

import MenuIcon from "../../../components/utils/table/icons/menu";
import { Container as MenuContainer } from "../../../components/utils/table/menu/styles";
import TagStatus from "../../../components/utils/tagStatus";
import { numberMask } from "../../../services/helpersMethodsService";
import {
  getColumnDateSearchProps,
  getColumnNumberWithBetweenSearchProps,
  getColumnSearchProps
} from "../../../utils/tableUtils";

const BoitelContractsTable = ({
  data,
  loading,
  translation,
  onTableChange,
  pagination,
  onOpenDetails,
  onDelete,
}) => {
  const handleSearch = useCallback((selectedKeys, confirm) => {
    confirm();
  }, []);

  const handleReset = useCallback((clearFilters) => {
    clearFilters();
  }, []);

  const handleDelete = (record) => {
    if (onDelete) onDelete(record);
  };

  const menuDropdown = (record) => {
    return (
      <Menu>
        {record.status === "PENDENTE" && (
          <Menu.Item key="0">
            <Link to={`/admin/boitel/contracts/${record.id}/edit`}>
              {translation.table.menu.edit}
            </Link>
          </Menu.Item>
        )}
        {record.status !== "ENCERRADO" && record.status !== "ERRO" && (
          <Menu.Item key="1">
            <Link to={`/admin/boitel/contracts/${record.id}/select-animals`}>
              {translation.boitelContractsPage.table.menu.selectAnimals}
            </Link>
          </Menu.Item>
        )}
        <Menu.Item
          key="2"
          onClick={() => onOpenDetails && onOpenDetails(record)}
        >
          {translation.table.menu.details}
        </Menu.Item>
        {record.status === "PENDENTE" && (
          <Menu.Item key="3" onClick={() => handleDelete(record)}>
            {translation.table.menu.delete}
          </Menu.Item>
        )}
      </Menu>
    );
  };

  const getColumnTextSearchProps = useCallback(
    (dataIndex) => getColumnSearchProps(dataIndex, handleReset, handleSearch),
    [handleReset, handleSearch],
  );

  const getColumnDateFilterProps = useCallback(
    (dataIndex) =>
      getColumnDateSearchProps(dataIndex, handleReset, handleSearch),
    [handleReset, handleSearch],
  );

  const getColumnNumberFilterProps = useCallback(
    (dataIndex) =>
      getColumnNumberWithBetweenSearchProps(
        dataIndex,
        handleReset,
        handleSearch,
      ),
    [handleReset, handleSearch],
  );

  const renderStatus = (status) => {
    const statusConfig = {
      VIGENTE: {
        text: translation.boitelContractsPage.table.column.statusOptions
          .vigente,
        background: "#C5F1CA",
        borderColor: "#106518",
        color: "#106518",
      },
      ENCERRADO: {
        text: translation.boitelContractsPage.table.column.statusOptions
          .encerrado,
        background: "#E6F7FF",
        borderColor: "#1890FF",
        color: "#1890FF",
      },
      PENDENTE: {
        text: translation.boitelContractsPage.table.column.statusOptions
          .pendente,
        background: "#FFF7E6",
        borderColor: "#FA8C16",
        color: "#FA8C16",
      },
      ERRO: {
        text: translation.boitelContractsPage.table.column.statusOptions.erro,
        background: "#FBC7C7",
        borderColor: "#D44C4C",
        color: "#D44C4C",
      },
    };

    const config = statusConfig[status] || statusConfig.VIGENTE;

    return (
      <TagStatus
        background={config.background}
        borderColor={config.borderColor}
        color={config.color}
      >
        {config.text}
      </TagStatus>
    );
  };

  const renderAnimalGender = (gender) => {
    const genderConfig = {
      V: translation.boitelContractsPage.table.column.animalGenderOptions.vaca,
      N: translation.boitelContractsPage.table.column.animalGenderOptions
        .novilha,
      I: translation.boitelContractsPage.table.column.animalGenderOptions
        .machoInteiro,
      C: translation.boitelContractsPage.table.column.animalGenderOptions
        .machoCastrado,
    };

    return genderConfig[gender] || gender;
  };

  const renderContractType = (contractType) => {
    const typeConfig = {
      daily:
        translation.boitelContractsPage.table.column.contractTypeOptions.daily,
      dryMatterConsume:
        translation.boitelContractsPage.table.column.contractTypeOptions
          .dryMatterConsume,
      arrobaProduction:
        translation.boitelContractsPage.table.column.contractTypeOptions
          .arrobaProduction,
      partnership:
        translation.boitelContractsPage.table.column.contractTypeOptions
          .partnership,
    };

    return typeConfig[contractType] || contractType;
  };

  return (
    <Table
      rowKey="id"
      size="small"
      dataSource={data?.content || []}
      loading={loading}
      pagination={{
        total: data?.totalElements || 0,
        size: data?.size || 0,
        current: data?.number ? data.number + 1 : 1,
        showSizeChanger: true,
        pageSizeOptions: ["10", "20", "30", "100", "500", "1000", "2000"],
        hideOnSinglePage:
          data !== null && Object.entries(data).length !== 0
            ? data.totalElements > 10
              ? false
              : true
            : true,
      }}
      style={{ width: "100%", margin: "0" }}
      onChange={onTableChange}
      scroll={{ x: true }}
    >
      <Table.Column
        title={
          translation.boitelContractsPage.table.column.identificationContract
        }
        dataIndex="identificationContract"
        key="identificationContract"
        align="left"
        sorter
        {...getColumnTextSearchProps("identificationContract")}
      />
      <Table.Column
        title={translation.boitelContractsPage.table.column.customerName}
        dataIndex="cattleProducerName"
        key="cattleProducerName"
        align="left"
        sorter
        {...getColumnTextSearchProps("cattleProducerName")}
      />
      <Table.Column
        title={translation.boitelContractsPage.table.column.contractType}
        dataIndex="contractType"
        key="contractType"
        align="left"
        sorter
        filtered
        filters={[
          {
            text: translation.boitelContractsPage.table.column
              .contractTypeOptions.daily,
            value: "daily",
          },
          {
            text: translation.boitelContractsPage.table.column
              .contractTypeOptions.dryMatterConsume,
            value: "dryMatterConsume",
          },
          {
            text: translation.boitelContractsPage.table.column
              .contractTypeOptions.arrobaProduction,
            value: "arrobaProduction",
          },
          {
            text: translation.boitelContractsPage.table.column
              .contractTypeOptions.partnership,
            value: "partnership",
          },
        ]}
        render={renderContractType}
      />
      <Table.Column
        title={translation.boitelContractsPage.table.column.dateContract}
        dataIndex="contractDate"
        key="contractDate"
        align="left"
        sorter
        {...getColumnDateFilterProps("contractDate")}
        render={(date) => (date ? moment(date).format("DD/MM/YYYY") : "-")}
      />
      <Table.Column
        title={translation.boitelContractsPage.table.column.finalDateContract}
        dataIndex="finalDateContract"
        key="finalDateContract"
        align="left"
        sorter
        {...getColumnDateFilterProps("finalDateContract")}
        render={(date) => (date ? moment(date).format("DD/MM/YYYY") : "-")}
      />
      <Table.Column
        title={translation.boitelContractsPage.table.column.status}
        dataIndex="status"
        key="status"
        align="left"
        sorter
        filtered
        filters={[
          {
            text: translation.boitelContractsPage.table.column.statusOptions
              .vigente,
            value: "VIGENTE",
          },
          {
            text: translation.boitelContractsPage.table.column.statusOptions
              .encerrado,
            value: "ENCERRADO",
          },
          {
            text: translation.boitelContractsPage.table.column.statusOptions
              .pendente,
            value: "PENDENTE",
          },
          {
            text: translation.boitelContractsPage.table.column.statusOptions
              .erro,
            value: "ERRO",
          },
        ]}
        render={renderStatus}
      />
      <Table.Column
        title={translation.boitelContractsPage.table.column.amountAnimals}
        dataIndex="amountAnimals"
        key="amountAnimals"
        align="right"
        sorter
        {...getColumnNumberFilterProps("amountAnimals")}
      />
      <Table.Column
        title={translation.boitelContractsPage.table.column.selectedAnimals}
        dataIndex="selectedAnimals"
        key="selectedAnimals"
        align="right"
        render={(value) => (value != null ? value : "-")}
      />
      <Table.Column
        title={translation.boitelContractsPage.table.column.animalGender}
        dataIndex="animalGender"
        key="animalGender"
        align="left"
        sorter
        filtered
        filters={[
          {
            text: translation.boitelContractsPage.table.column
              .animalGenderOptions.vaca,
            value: "V",
          },
          {
            text: translation.boitelContractsPage.table.column
              .animalGenderOptions.novilha,
            value: "N",
          },
          {
            text: translation.boitelContractsPage.table.column
              .animalGenderOptions.machoInteiro,
            value: "I",
          },
          {
            text: translation.boitelContractsPage.table.column
              .animalGenderOptions.machoCastrado,
            value: "C",
          },
        ]}
        render={renderAnimalGender}
      />
      <Table.Column
        title={translation.boitelContractsPage.table.column.entryWeight}
        dataIndex="entryWeight"
        key="entryWeight"
        align="right"
        sorter
        {...getColumnNumberFilterProps("entryWeight")}
        render={(weight) => (weight ? numberMask(weight, false) : "-")}
      />
      <Table.Column
        title={translation.boitelContractsPage.table.column.contractDays}
        dataIndex="contractDays"
        key="contractDays"
        align="right"
        sorter
        {...getColumnNumberFilterProps("contractDays")}
      />
      <Table.Column
        title={translation.boitelContractsPage.table.column.consecutiveDays}
        dataIndex="consecutiveDays"
        key="consecutiveDays"
        align="right"
        sorter
        {...getColumnNumberFilterProps("consecutiveDays")}
      />
      <Table.Column
        title={translation.boitelContractsPage.table.column.fileUrl}
        dataIndex="fileUrl"
        key="fileUrl"
        align="left"
        render={(fileUrl) =>
          fileUrl ? (
            <a href={fileUrl} target="_blank" rel="noopener noreferrer">
              <Icon type="file" />
            </a>
          ) : (
            "-"
          )
        }
      />
      <Table.Column
        align="left"
        width={50}
        render={(text, record) => (
          <Dropdown
            overlay={menuDropdown(record)}
            trigger={["click"]}
            key={record.id}
          >
            <MenuContainer>
              <MenuIcon />
            </MenuContainer>
          </Dropdown>
        )}
      />
    </Table>
  );
};

export default BoitelContractsTable;
